import { oc } from "@orpc/contract";
import { LanguageIds } from "@packages/core/language/language.ts";
import { z } from "zod";

const ManagedPersonStatuses = ["active", "created", "invited", "deactivated", "nonSeat"] as const;

export type ManagedPersonStatus = (typeof ManagedPersonStatuses)[number];

export const FilterableManagedPersonStatuses = ManagedPersonStatuses;

export const ManagedPersonRoles = ["admin", "executive", "collaborator", "manager", "employee"] as const;

export type ManagedPersonRole = (typeof ManagedPersonRoles)[number];

export const ManagedPeopleSortColumns = ["name", "roles", "hireDate", "status", "lastSession"] as const;

export type ManagedPeopleSortColumn = (typeof ManagedPeopleSortColumns)[number];

export const GrantableManagedPersonRoles = ["admin", "executive", "collaborator"] as const;

export type GrantableManagedPersonRole = (typeof GrantableManagedPersonRoles)[number];

const ManagedPersonSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    jobTitle: z.string().optional(),
    imageUrl: z.string().optional(),
    roles: z.array(z.enum(ManagedPersonRoles)),
    hireDate: z.string().optional(),
    status: z.enum(ManagedPersonStatuses),
    lastSessionDate: z.string().optional()
});

export type ManagedPerson = z.infer<typeof ManagedPersonSchema>;

const CommunicationLanguageOptionSchema = z.object({
    code: z.string(),
    nativeName: z.string()
});

export type CommunicationLanguageOption = z.infer<typeof CommunicationLanguageOptionSchema>;

const ManagedPersonDetailSchema = ManagedPersonSchema.extend({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    preferredName: z.string().optional(),
    employeeId: z.string().optional(),
    dateOfBirth: z.string().optional(),
    pronouns: z.string().optional(),
    email: z.string().optional(),
    officePhone: z.string().optional(),
    genderId: z.string().optional(),
    genderLabel: z.string().optional(),
    workLocationId: z.string().optional(),
    workLocationLabel: z.string().optional(),
    timeZone: z.string().optional(),
    cityId: z.string().optional(),
    cityLabel: z.string().optional(),
    regionId: z.string().optional(),
    regionLabel: z.string().optional(),
    countryId: z.string().optional(),
    countryLabel: z.string().optional(),
    preferredLanguage: z.enum(LanguageIds).optional(),
    communicationLanguage: z.string().nullable(),
    communicationLanguageOptions: z.array(CommunicationLanguageOptionSchema),
    bio: z.string().optional(),
    interests: z.array(z.string()),
    competencies: z.array(z.string()),
    isImageLocked: z.boolean(),
    isFirstNameLocked: z.boolean(),
    isLastNameLocked: z.boolean(),
    isEmailLocked: z.boolean(),
    isGenderLocked: z.boolean(),
    isPronounsLocked: z.boolean(),
    isDateOfBirthLocked: z.boolean(),
    isCityLocked: z.boolean(),
    isRegionLocked: z.boolean(),
    isCountryLocked: z.boolean(),
    isWorkLocationLocked: z.boolean(),
    isInterestsLocked: z.boolean(),
    isCompetenciesLocked: z.boolean(),
    syncedIntegrationName: z.string().optional()
});

export type ManagedPersonDetail = z.infer<typeof ManagedPersonDetailSchema>;

const DateOfBirthSchema = z.string().refine(value => value.slice(0, 10) <= new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10), { message: "Birthday can't be in the future." });

const OptionIdSchema = z.string().regex(/^\d+$/);

const UpdatePersonInputSchema = z.object({
    personId: z.string(),
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    email: z.email().optional(),
    preferredName: z.string().min(1).nullable().optional(),
    employeeId: z.string().min(1).nullable().optional(),
    dateOfBirth: DateOfBirthSchema.nullable().optional(),
    pronouns: z.string().min(1).nullable().optional(),
    officePhone: z.string().min(1).nullable().optional(),
    genderId: OptionIdSchema.nullable().optional(),
    workLocationId: OptionIdSchema.nullable().optional(),
    timeZone: z.string().min(1).nullable().optional(),
    cityId: OptionIdSchema.nullable().optional(),
    regionId: OptionIdSchema.nullable().optional(),
    countryId: OptionIdSchema.nullable().optional(),
    bio: z.string().min(1).nullable().optional(),
    interests: z.array(z.string()).optional(),
    competencies: z.array(z.string()).optional(),
    preferredLanguage: z.enum(LanguageIds).nullable().optional(),
    communicationLanguage: z.string().nullable().optional()
});

export type UpdatePersonInput = z.infer<typeof UpdatePersonInputSchema>;

const UpdatePersonOutputSchema = z.object({
    communicationLanguageSaveFailed: z.boolean()
});

export const PERSON_PHOTO_MAX_BYTES = 5 * 1024 * 1024;

const UpdatePersonPhotoInputSchema = z.object({
    personId: z.string(),
    imageBase64String: z.string().min(1)
});

const PeopleFilterSchema = z.object({
    q: z.string().optional(),
    statuses: z.array(z.enum(FilterableManagedPersonStatuses)).optional(),
    roles: z.array(z.enum(ManagedPersonRoles)).optional(),
    departmentIds: z.array(z.string()).optional(),
    managerIds: z.array(z.string()).optional(),
    jobIds: z.array(z.string()).optional()
});

const ListPeopleInputSchema = PeopleFilterSchema.extend({
    orgId: z.string(),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(25),
    sortBy: z.enum(ManagedPeopleSortColumns).default("name"),
    sortDir: z.enum(["asc", "desc"]).default("asc")
});

const ListPeopleOutputSchema = z.object({
    items: z.array(ManagedPersonSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

export type ListPeopleInput = z.infer<typeof ListPeopleInputSchema>;
export type ListPeopleOutput = z.infer<typeof ListPeopleOutputSchema>;

const CountPeopleByStateInputSchema = PeopleFilterSchema.extend({
    orgId: z.string(),
    excludedIds: z.array(z.string())
});

const CountPeopleByStateOutputSchema = z.object({
    activeCount: z.number(),
    deactivatedCount: z.number(),
    createdCount: z.number(),
    invitedCount: z.number(),
    nonSeatCount: z.number()
});

export type CountPeopleByStateInput = z.infer<typeof CountPeopleByStateInputSchema>;
export type CountPeopleByStateOutput = z.infer<typeof CountPeopleByStateOutputSchema>;

const FacetSchema = z.object({
    id: z.string(),
    label: z.string()
});

export type Facet = z.infer<typeof FacetSchema>;

const ListDepartmentsInputSchema = z.object({
    orgId: z.string()
});

const ListDepartmentsOutputSchema = z.object({
    departments: z.array(FacetSchema)
});

export type ListDepartmentsOutput = z.infer<typeof ListDepartmentsOutputSchema>;

const ListDivisionsInputSchema = z.object({
    orgId: z.string()
});

const ListDivisionsOutputSchema = z.object({
    divisions: z.array(FacetSchema)
});

export type ListDivisionsOutput = z.infer<typeof ListDivisionsOutputSchema>;

const ListDepartmentOptionsInputSchema = z.object({
    orgId: z.string()
});

const ListDepartmentOptionsOutputSchema = z.object({
    departments: z.array(FacetSchema)
});

export type ListDepartmentOptionsOutput = z.infer<typeof ListDepartmentOptionsOutputSchema>;

const PersonPropertySchema = z.object({
    id: z.string().optional(),
    systemAttributeId: z.string().optional(),
    displayName: z.string(),
    options: z.array(FacetSchema)
});

export type PersonProperty = z.infer<typeof PersonPropertySchema>;

const ListPersonPropertiesInputSchema = z.object({
    orgId: z.string()
});

const ListPersonPropertiesOutputSchema = z.object({
    properties: z.array(PersonPropertySchema)
});

export type ListPersonPropertiesOutput = z.infer<typeof ListPersonPropertiesOutputSchema>;

const PersonCustomPropertyTypes = ["option", "text", "numeric", "dateTime", "guid", "tag"] as const;

export type PersonCustomPropertyType = (typeof PersonCustomPropertyTypes)[number];

const PersonCustomPropertySyncSources = ["o365", "bambooHr", "merge", "google"] as const;

export type PersonCustomPropertySyncSource = (typeof PersonCustomPropertySyncSources)[number];

const PersonCustomPropertyBaseFields = {
    propertyId: z.string(),
    displayName: z.string(),
    isSynced: z.boolean(),
    syncSource: z.enum(PersonCustomPropertySyncSources).optional(),
    isRestricted: z.boolean()
};

const PersonCustomPropertyReferenceSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    imageUrl: z.string().optional()
});

const PersonCustomPropertySchema = z.discriminatedUnion("type", [
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("option"), value: OptionIdSchema.nullable() }),
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("text"), value: z.string().nullable() }),
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("numeric"), value: z.number().nullable() }),
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("dateTime"), value: z.string().nullable() }),
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("guid"), value: z.string().nullable(), person: PersonCustomPropertyReferenceSchema.nullable() }),
    z.object({ ...PersonCustomPropertyBaseFields, type: z.literal("tag"), value: z.array(z.string()) })
]);

export type PersonCustomProperty = z.infer<typeof PersonCustomPropertySchema>;

const GetPersonCustomPropertiesOutputSchema = z.object({
    properties: z.array(PersonCustomPropertySchema),
    syncedIntegrationName: z.string().optional()
});

export type GetPersonCustomPropertiesOutput = z.infer<typeof GetPersonCustomPropertiesOutputSchema>;

const UpdatePersonCustomPropertyInputSchema = z.discriminatedUnion("type", [
    z.object({ propertyId: z.string(), type: z.literal("option"), value: OptionIdSchema.nullable() }),
    z.object({ propertyId: z.string(), type: z.literal("text"), value: z.string().min(1).nullable() }),
    z.object({ propertyId: z.string(), type: z.literal("numeric"), value: z.number().nullable() }),
    z.object({ propertyId: z.string(), type: z.literal("dateTime"), value: z.string().nullable() }),
    z.object({ propertyId: z.string(), type: z.literal("guid"), value: z.string().nullable() }),
    z.object({ propertyId: z.string(), type: z.literal("tag"), value: z.array(z.string()) })
]);

export type UpdatePersonCustomPropertyInput = z.infer<typeof UpdatePersonCustomPropertyInputSchema>;

const UpdatePersonCustomPropertiesInputSchema = z.object({
    personId: z.string(),
    properties: z.array(UpdatePersonCustomPropertyInputSchema).min(1)
});

export type UpdatePersonCustomPropertiesInput = z.infer<typeof UpdatePersonCustomPropertiesInputSchema>;

const ManagerCandidateSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    imageUrl: z.string().optional()
});

export type ManagerCandidate = z.infer<typeof ManagerCandidateSchema>;

const SearchManagersInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    continuationToken: z.string().optional()
});

export type SearchManagersInput = z.infer<typeof SearchManagersInputSchema>;

const SearchManagersOutputSchema = z.object({
    items: z.array(ManagerCandidateSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

export type SearchManagersOutput = z.infer<typeof SearchManagersOutputSchema>;

const SearchJobTitlesInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    continuationToken: z.string().optional()
});

export type SearchJobTitlesInput = z.infer<typeof SearchJobTitlesInputSchema>;

const SearchJobTitlesOutputSchema = z.object({
    items: z.array(FacetSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

export type SearchJobTitlesOutput = z.infer<typeof SearchJobTitlesOutputSchema>;

const PersonActionInputSchema = z.object({
    personId: z.string()
});

const ManagedPersonJobInformationSchema = z.object({
    personId: z.string(),
    department: FacetSchema.nullable(),
    division: FacetSchema.nullable(),
    manager: ManagerCandidateSchema.nullable(),
    hireDate: z.string().nullable(),
    terminationDate: z.string().nullable(),
    terminationReason: z.string().nullable(),
    isEmploymentsLocked: z.boolean(),
    isDepartmentLocked: z.boolean(),
    isDivisionLocked: z.boolean(),
    isManagerLocked: z.boolean(),
    isHireDateLocked: z.boolean(),
    isTerminationDateLocked: z.boolean(),
    isTerminationReasonLocked: z.boolean(),
    syncedIntegrationName: z.string().optional()
});

export type ManagedPersonJobInformation = z.infer<typeof ManagedPersonJobInformationSchema>;

const UpdatePersonJobInformationInputSchema = z
    .object({
        personId: z.string(),
        departmentId: z.string().nullable().optional(),
        divisionId: z.string().nullable().optional(),
        managerId: z.string().nullable().optional(),
        hireDate: z.string().nullable().optional(),
        terminationDate: z.string().nullable().optional(),
        terminationReason: z.string().nullable().optional()
    })
    .superRefine((value, ctx) => {
        if (value.managerId && value.managerId.toLowerCase() === value.personId.toLowerCase()) {
            ctx.addIssue({ code: "custom", path: ["managerId"], message: "A person can't be their own manager." });
        }

        if (value.hireDate && value.terminationDate && value.terminationDate < value.hireDate) {
            ctx.addIssue({ code: "custom", path: ["terminationDate"], message: "Termination date can't be before the hire date." });
        }
    });

export type UpdatePersonJobInformationInput = z.infer<typeof UpdatePersonJobInformationInputSchema>;

export const EmploymentTypes = ["FullTime", "PartTime", "Intern", "Contractor", "Freelance", "Other"] as const;

export type EmploymentType = (typeof EmploymentTypes)[number];

export const FlsaStatuses = ["Exempt", "SalariedNonexempt", "Nonexempt", "Owner", "Other"] as const;

export type FlsaStatus = (typeof FlsaStatuses)[number];

const PersonEmploymentEntrySchema = z.object({
    id: z.string(),
    jobTitle: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    employmentType: z.enum(EmploymentTypes).nullable(),
    hoursPerWeek: z.number().nullable(),
    flsaStatus: z.enum(FlsaStatuses).nullable(),
    isCurrent: z.boolean()
});

export type PersonEmploymentEntry = z.infer<typeof PersonEmploymentEntrySchema>;

const ListPersonEmploymentHistoryOutputSchema = z.object({
    personFirstName: z.string().nullable(),
    entries: z.array(PersonEmploymentEntrySchema)
});

export type ListPersonEmploymentHistoryOutput = z.infer<typeof ListPersonEmploymentHistoryOutputSchema>;

const PersonEmploymentEntryInputSchema = z.object({
    jobTitle: z.string().min(1),
    effectiveDate: z.string(),
    employmentType: z.enum(EmploymentTypes),
    hoursPerWeek: z.number().positive(),
    flsaStatus: z.enum(FlsaStatuses).optional()
});

const AddPersonEmploymentEntryInputSchema = PersonEmploymentEntryInputSchema.extend({
    personId: z.string()
});

export type AddPersonEmploymentEntryInput = z.infer<typeof AddPersonEmploymentEntryInputSchema>;

const UpdatePersonEmploymentEntryInputSchema = PersonEmploymentEntryInputSchema.extend({
    personId: z.string(),
    employmentId: z.string()
});

export type UpdatePersonEmploymentEntryInput = z.infer<typeof UpdatePersonEmploymentEntryInputSchema>;

const RemovePersonEmploymentEntryInputSchema = z.object({
    personId: z.string(),
    employmentId: z.string()
});

export type RemovePersonEmploymentEntryInput = z.infer<typeof RemovePersonEmploymentEntryInputSchema>;

const BulkPersonActionInputSchema = z.union([z.object({ personIds: z.array(z.string()).min(1) }), PeopleFilterSchema.extend({ excludedIds: z.array(z.string()) })]);

export type BulkPersonActionInput = z.infer<typeof BulkPersonActionInputSchema>;

const RoleChangesSchema = z.object({
    admin: z.boolean().optional(),
    executive: z.boolean().optional(),
    collaborator: z.boolean().optional()
});

export type RoleChanges = z.infer<typeof RoleChangesSchema>;

const ManagePermissionsInputSchema = BulkPersonActionInputSchema.and(z.object({ roleChanges: RoleChangesSchema }));

export type ManagePermissionsInput = z.infer<typeof ManagePermissionsInputSchema>;

const RoleCompositions = ["all", "none", "mixed"] as const;

export type RoleComposition = (typeof RoleCompositions)[number];

const RolesCompositionSchema = z.object({
    admin: z.enum(RoleCompositions),
    executive: z.enum(RoleCompositions),
    collaborator: z.enum(RoleCompositions)
});

export type RolesComposition = z.infer<typeof RolesCompositionSchema>;

const OrganizationSyncProviders = ["merge", "slack", "google", "microsoft365", "excel", "csv"] as const;

export type OrganizationSyncProvider = (typeof OrganizationSyncProviders)[number];

const OrganizationSyncStatusSchema = z.object({
    isActive: z.boolean(),
    integrationName: z.string().optional(),
    provider: z.enum(OrganizationSyncProviders).optional(),
    integrationSlug: z.string().optional(),
    lastSyncDate: z.string().optional()
});

export type OrganizationSyncStatus = z.infer<typeof OrganizationSyncStatusSchema>;

const SendInvitePeopleOutputSchema = z.object({
    invitedCount: z.number(),
    totalCount: z.number()
});

export type SendInvitePeopleOutput = z.infer<typeof SendInvitePeopleOutputSchema>;

const ResendInvitePeopleOutputSchema = z.object({
    resentCount: z.number(),
    totalCount: z.number()
});

export type ResendInvitePeopleOutput = z.infer<typeof ResendInvitePeopleOutputSchema>;

export const INVITE_EMAILS_MAX = 50;

const InvitePeopleInputSchema = z.object({
    emails: z.array(z.email()).min(1).max(INVITE_EMAILS_MAX),
    managerId: z.string().optional(),
    grantedRoles: z.array(z.enum(GrantableManagedPersonRoles))
});

export type InvitePeopleInput = z.infer<typeof InvitePeopleInputSchema>;

const EmailValidationReasons = ["alreadyMember", "usedElsewhere", "maxOrganizations", "invalidFormat", "unknown"] as const;

export type EmailValidationReason = (typeof EmailValidationReasons)[number];

const EmailValidationResultSchema = z.object({
    email: z.string(),
    isValid: z.boolean(),
    reason: z.enum(EmailValidationReasons).optional()
});

export type EmailValidationResult = z.infer<typeof EmailValidationResultSchema>;

const ValidateEmailsInputSchema = z.object({
    emails: z.array(z.email()).min(1).max(INVITE_EMAILS_MAX),
    checkOtherOrganizations: z.boolean().optional(),
    excludedPersonIds: z.array(z.string()).optional()
});

export type ValidateEmailsInput = z.infer<typeof ValidateEmailsInputSchema>;

const ValidateEmailsOutputSchema = z.object({
    results: z.array(EmailValidationResultSchema)
});

export type ValidateEmailsOutput = z.infer<typeof ValidateEmailsOutputSchema>;

export const NON_SEAT_NAMES_MAX = 50;

const CreateNonSeatPeopleInputSchema = z.object({
    names: z.array(z.string().trim().min(1)).min(1).max(NON_SEAT_NAMES_MAX),
    managerId: z.string().optional()
});

export const INVITE_PHONE_NUMBERS_MAX = 50;

const PhoneNumberSchema = z.e164();

const InvitePeopleByTextInputSchema = z.object({
    phoneNumbers: z.array(PhoneNumberSchema).min(1).max(INVITE_PHONE_NUMBERS_MAX),
    managerId: z.string().optional(),
    grantedRoles: z.array(z.enum(GrantableManagedPersonRoles))
});

export type InvitePeopleByTextInput = z.infer<typeof InvitePeopleByTextInputSchema>;

const PhoneNumberValidationReasons = ["alreadyMember", "invalidFormat", "unknown"] as const;

export type PhoneNumberValidationReason = (typeof PhoneNumberValidationReasons)[number];

const PhoneNumberValidationResultSchema = z.object({
    phoneNumber: z.string(),
    isValid: z.boolean(),
    reason: z.enum(PhoneNumberValidationReasons).optional()
});

export type PhoneNumberValidationResult = z.infer<typeof PhoneNumberValidationResultSchema>;

const ValidatePhoneNumbersInputSchema = z.object({
    phoneNumbers: z.array(PhoneNumberSchema).min(1).max(INVITE_PHONE_NUMBERS_MAX)
});

export type ValidatePhoneNumbersInput = z.infer<typeof ValidatePhoneNumbersInputSchema>;

const ValidatePhoneNumbersOutputSchema = z.object({
    results: z.array(PhoneNumberValidationResultSchema)
});

export type ValidatePhoneNumbersOutput = z.infer<typeof ValidatePhoneNumbersOutputSchema>;

export const managePeopleContract = {
    listPeople: oc.route({ method: "GET", path: "/list-people" }).errors({ UPSTREAM_FAILURE: {} }).input(ListPeopleInputSchema).output(ListPeopleOutputSchema),
    getPerson: oc
        .route({ method: "GET", path: "/get-person" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(ManagedPersonDetailSchema),
    countPeopleByState: oc.route({ method: "GET", path: "/count-people-by-state" }).errors({ UPSTREAM_FAILURE: {} }).input(CountPeopleByStateInputSchema).output(CountPeopleByStateOutputSchema),
    getOrganizationSyncStatus: oc.route({ method: "GET", path: "/get-organization-sync-status" }).output(OrganizationSyncStatusSchema),
    managePermissions: oc
        .route({ method: "POST", path: "/manage-permissions" })
        .errors({ UPSTREAM_FAILURE: {}, SELF_ADMIN_LOCKED: { status: 400 }, ADMIN_ROLE_LOCKED: { status: 400 } })
        .input(ManagePermissionsInputSchema)
        .output(z.void()),
    getRolesComposition: oc.route({ method: "GET", path: "/get-roles-composition" }).errors({ UPSTREAM_FAILURE: {} }).input(CountPeopleByStateInputSchema).output(RolesCompositionSchema),
    listDepartments: oc.route({ method: "GET", path: "/list-departments" }).errors({ UPSTREAM_FAILURE: {} }).input(ListDepartmentsInputSchema).output(ListDepartmentsOutputSchema),
    listDivisions: oc.route({ method: "GET", path: "/list-divisions" }).errors({ UPSTREAM_FAILURE: {} }).input(ListDivisionsInputSchema).output(ListDivisionsOutputSchema),
    listDepartmentOptions: oc.route({ method: "GET", path: "/list-department-options" }).errors({ UPSTREAM_FAILURE: {} }).input(ListDepartmentOptionsInputSchema).output(ListDepartmentOptionsOutputSchema),
    getPersonJobInformation: oc
        .route({ method: "GET", path: "/get-person-job-information" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(ManagedPersonJobInformationSchema),
    updatePersonJobInformation: oc
        .route({ method: "POST", path: "/update-person-job-information" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(UpdatePersonJobInformationInputSchema)
        .output(z.void()),
    listPersonEmploymentHistory: oc
        .route({ method: "GET", path: "/list-person-employment-history" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(ListPersonEmploymentHistoryOutputSchema),
    addPersonEmploymentEntry: oc
        .route({ method: "POST", path: "/add-person-employment-entry" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(AddPersonEmploymentEntryInputSchema)
        .output(z.void()),
    updatePersonEmploymentEntry: oc
        .route({ method: "POST", path: "/update-person-employment-entry" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(UpdatePersonEmploymentEntryInputSchema)
        .output(z.void()),
    removePersonEmploymentEntry: oc
        .route({ method: "POST", path: "/remove-person-employment-entry" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(RemovePersonEmploymentEntryInputSchema)
        .output(z.void()),
    listPersonProperties: oc.route({ method: "GET", path: "/list-person-properties" }).errors({ UPSTREAM_FAILURE: {} }).input(ListPersonPropertiesInputSchema).output(ListPersonPropertiesOutputSchema),
    getPersonCustomProperties: oc
        .route({ method: "GET", path: "/get-person-custom-properties" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(GetPersonCustomPropertiesOutputSchema),
    updatePersonCustomProperties: oc
        .route({ method: "POST", path: "/update-person-custom-properties" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(UpdatePersonCustomPropertiesInputSchema)
        .output(z.void()),
    searchManagers: oc.route({ method: "GET", path: "/search-managers" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchManagersInputSchema).output(SearchManagersOutputSchema),
    searchManagerCandidates: oc.route({ method: "GET", path: "/search-manager-candidates" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchManagersInputSchema).output(SearchManagersOutputSchema),
    searchJobTitles: oc.route({ method: "GET", path: "/search-job-titles" }).errors({ UPSTREAM_FAILURE: {} }).input(SearchJobTitlesInputSchema).output(SearchJobTitlesOutputSchema),
    deactivatePerson: oc
        .route({ method: "POST", path: "/deactivate-person" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(z.void()),
    reactivatePerson: oc
        .route({ method: "POST", path: "/reactivate-person" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(z.void()),
    sendInvite: oc
        .route({ method: "POST", path: "/send-invite" })
        .errors({ UPSTREAM_FAILURE: {}, INVITE_RECENTLY_SENT: { status: 400 }, NO_INVITABLE_PRODUCT: { status: 400 } })
        .input(PersonActionInputSchema)
        .output(z.void()),
    resendInvite: oc
        .route({ method: "POST", path: "/resend-invite" })
        .errors({ UPSTREAM_FAILURE: {}, INVITE_RECENTLY_SENT: { status: 400 }, NO_INVITABLE_PRODUCT: { status: 400 } })
        .input(PersonActionInputSchema)
        .output(z.void()),
    removePerson: oc
        .route({ method: "POST", path: "/remove-person" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(z.void()),
    updatePerson: oc
        .route({ method: "POST", path: "/update-person" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(UpdatePersonInputSchema)
        .output(UpdatePersonOutputSchema),
    updatePersonPhoto: oc
        .route({ method: "POST", path: "/update-person-photo" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {}, INVALID_IMAGE_FORMAT: { status: 400 }, IMAGE_TOO_LARGE: { status: 400 } })
        .input(UpdatePersonPhotoInputSchema)
        .output(z.void()),
    removePersonPhoto: oc
        .route({ method: "POST", path: "/remove-person-photo" })
        .errors({ NOT_FOUND: { data: z.object({ personId: z.string() }) }, UPSTREAM_FAILURE: {} })
        .input(PersonActionInputSchema)
        .output(z.void()),
    deactivatePeople: oc.route({ method: "POST", path: "/deactivate-people" }).errors({ UPSTREAM_FAILURE: {} }).input(BulkPersonActionInputSchema).output(z.void()),
    reactivatePeople: oc.route({ method: "POST", path: "/reactivate-people" }).errors({ UPSTREAM_FAILURE: {} }).input(BulkPersonActionInputSchema).output(z.void()),
    sendInvitePeople: oc.route({ method: "POST", path: "/send-invite-people" }).errors({ UPSTREAM_FAILURE: {} }).input(BulkPersonActionInputSchema).output(SendInvitePeopleOutputSchema),
    resendInvitePeople: oc.route({ method: "POST", path: "/resend-invite-people" }).errors({ UPSTREAM_FAILURE: {} }).input(BulkPersonActionInputSchema).output(ResendInvitePeopleOutputSchema),
    removePeople: oc.route({ method: "POST", path: "/remove-people" }).errors({ UPSTREAM_FAILURE: {} }).input(BulkPersonActionInputSchema).output(z.void()),
    validateEmails: oc.route({ method: "POST", path: "/validate-emails" }).errors({ UPSTREAM_FAILURE: {} }).input(ValidateEmailsInputSchema).output(ValidateEmailsOutputSchema),
    invitePeopleByEmail: oc.route({ method: "POST", path: "/invite-people-by-email" }).errors({ UPSTREAM_FAILURE: {} }).input(InvitePeopleInputSchema).output(z.void()),
    createPeopleWithoutInvite: oc.route({ method: "POST", path: "/create-people-without-invite" }).errors({ UPSTREAM_FAILURE: {} }).input(InvitePeopleInputSchema).output(z.void()),
    createNonSeatPeople: oc.route({ method: "POST", path: "/create-non-seat-people" }).errors({ UPSTREAM_FAILURE: {} }).input(CreateNonSeatPeopleInputSchema).output(z.void()),
    validatePhoneNumbers: oc.route({ method: "POST", path: "/validate-phone-numbers" }).errors({ UPSTREAM_FAILURE: {} }).input(ValidatePhoneNumbersInputSchema).output(ValidatePhoneNumbersOutputSchema),
    invitePeopleByText: oc
        .route({ method: "POST", path: "/invite-people-by-text" })
        .errors({ UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(InvitePeopleByTextInputSchema)
        .output(z.void()),
    createPeopleWithoutInviteByText: oc
        .route({ method: "POST", path: "/create-people-without-invite-by-text" })
        .errors({ UPSTREAM_FAILURE: {}, VALIDATION_FAILED: { status: 400 } })
        .input(InvitePeopleByTextInputSchema)
        .output(z.void())
};
